import React, { useRef, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Loader2 } from "lucide-react";

const TEMPLATE = "email,name,phone,role\nama.mensah@example.com,Ama Mensah,+233201234567,agent\nkofi.boateng@example.com,Kofi Boateng,+233241234567,observer\n";

export const BulkInvitePanel = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState("");
  const [sendInvite, setSendInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const r = new FileReader(); r.onload = () => setCsv(String(r.result || "")); r.readAsText(f);
  };

  const rows = csv.trim() ? Math.max(0, csv.trim().split(/\r?\n/).length - 1) : 0;

  const submit = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/users/bulk-import", { csv_text: csv, send_invite: sendInvite });
      setResult(data);
      toast.success(`${data.created} created, ${data.updated} updated${sendInvite ? ` — ${data.invites_queued} invite link${data.invites_queued === 1 ? "" : "s"} being sent` : ""}`);
      onDone?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" })); a.download = "collation_watch_users_template.csv"; a.click();
  };

  return (
    <div className="border border-white/10 mt-6" data-testid="bulk-invite-panel">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/[0.02]" data-testid="bulk-invite-toggle">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Bulk invites</div>
          <div className="text-sm mt-1">Upload a CSV of agents &amp; reviewers — each one gets a set-password link</div>
        </div>
        <FileSpreadsheet size={16} className="text-zinc-500"/>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => fileRef.current?.click()} className="border border-white/20 text-zinc-200 hover:bg-white hover:text-black text-xs uppercase tracking-[0.2em] font-semibold px-4 py-2 flex items-center gap-2" data-testid="bulk-pick-file-btn"><Upload size={14}/>Choose CSV</button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="bulk-file-input"/>
            <button onClick={downloadTemplate} className="text-xs text-zinc-400 hover:text-white underline underline-offset-4" data-testid="bulk-template-btn">Download template</button>
            <span className="text-[11px] text-zinc-500">Header: <span className="font-mono">email,name,phone,role</span> (phone & role optional; role = agent | observer | admin)</span>
          </div>
          <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={6} placeholder={TEMPLATE}
            className="w-full bg-transparent border border-white/15 p-3 text-xs font-mono outline-none focus:border-white/40" data-testid="bulk-csv-textarea"/>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none">
              <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} className="accent-emerald-500 w-4 h-4" data-testid="bulk-send-invite-checkbox"/>
              Email every new (and not-yet-signed-in) user a set-password link
            </label>
            <button onClick={submit} disabled={busy || rows === 0}
              className="bg-white text-black text-xs uppercase tracking-[0.2em] font-semibold px-4 py-2 hover:bg-zinc-200 disabled:opacity-40 flex items-center gap-2" data-testid="bulk-import-submit-btn">
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}{busy ? "Importing…" : `Import ${rows || ""} row${rows === 1 ? "" : "s"}`}
            </button>
          </div>
          {result && (
            <div className="text-xs text-zinc-300 border border-white/10 p-3" data-testid="bulk-import-result">
              <span className="text-emerald-300">{result.created} created</span> · {result.updated} updated · {result.invites_queued} invites sending · <span className={result.failed.length ? "text-red-300" : ""}>{result.failed.length} failed</span>
              {result.failed.length > 0 && <ul className="mt-2 space-y-1 text-red-300/90">{result.failed.map((f, i) => <li key={`${f.email || "blank"}-${i}`}>{f.email || "(blank)"} — {f.reason}</li>)}</ul>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
